import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../shared/store/auth.store.js'
import { ApiError } from '../shared/types/api.js'
import { ROUTES } from './routes.js'

export type PlatformApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'unknown'

export function classifyPlatformApiError(error: unknown): PlatformApiErrorKind {
  if (!(error instanceof ApiError)) return 'unknown'
  if (error.status === 401) return 'unauthorized'
  if (error.status === 403) return 'forbidden'
  if (error.status === 404) return 'notFound'
  if (error.status === 409) return 'conflict'
  return 'unknown'
}

/**
 * Central handler for platform page requests. A 401 clears the dead session and
 * sends the operator to login; other statuses are returned for localized UI.
 * Returns null when a redirect was issued.
 */
export function usePlatformApiError(): (error: unknown) => PlatformApiErrorKind | null {
  const navigate = useNavigate()
  const clear = useAuthStore(s => s.clear)

  return useCallback((error: unknown) => {
    const kind = classifyPlatformApiError(error)
    if (kind === 'unauthorized') {
      clear()
      navigate(ROUTES.LOGIN, { replace: true })
      return null
    }
    return kind
  }, [clear, navigate])
}
